// Helpers to compose FilterBar filter-chip arrays for the most common case:
//
//   "give me chips for pos_type / region / dystrykt / agent / RKS(A) / KR / XDM
//    populated from /admin/pos/distinct"
//
// Each report page calls `useDistinctFilters(operator)` once on mount, then
// `buildOplFilterFields(distinct, filters, setFilters, opts?)` (or `buildPlkFilterFields`,
// `buildTmplFilterFields`) to get the array passed to <FilterBar filters={…} />.
//
// Pages can append their own chips after these or set `omit` to skip specific keys.
import { useEffect, useState } from 'react'
import { apiService } from '../../services/api'

export function useDistinctFilters(operator) {
  const [data, setData] = useState({})
  useEffect(() => {
    let cancelled = false
    if (!operator) { setData({}); return }
    apiService.getAdminPosDistinct(operator)
      .then(r => { if (!cancelled) setData(r.data || {}) })
      .catch(() => { if (!cancelled) setData({}) })
    return () => { cancelled = true }
  }, [operator])
  return data
}

const optsWithBlank = (arr, blankLabel = 'Wszystkie') => [
  { value: null, label: blankLabel },
  ...((arr || []).filter(Boolean).map(v => ({ value: v, label: v }))),
]

const set = (filters, setFilters, key) => (v) =>
  setFilters({ ...filters, [key]: v || undefined })

// ─ OPL ────────────────────────────────────────────────────────
export function buildOplFilterFields(distinct, filters, setFilters, opts = {}) {
  const omit = new Set(opts.omit || [])
  const fields = []

  if (!omit.has('pos_type') && (distinct?.pos_types || []).length > 0) {
    fields.push({
      type: 'select', key: 'pos_type', label: 'Typ salonu',
      value: filters.pos_type ?? null,
      options: optsWithBlank(distinct.pos_types),
      onChange: set(filters, setFilters, 'pos_type'),
    })
  }
  if (!omit.has('region') && (distinct?.regions || []).length > 0) {
    fields.push({
      type: 'select', key: 'region', label: 'Region',
      value: filters.region ?? null,
      options: optsWithBlank(distinct.regions),
      onChange: set(filters, setFilters, 'region'),
    })
  }
  if (!omit.has('dystrykt') && (distinct?.districts || []).length > 0) {
    fields.push({
      type: 'select', key: 'dystrykt', label: 'Dystrykt',
      value: filters.dystrykt ?? null,
      options: optsWithBlank(distinct.districts),
      onChange: set(filters, setFilters, 'dystrykt'),
    })
  }
  if (!omit.has('agent') && (distinct?.agents || []).length > 0) {
    fields.push({
      type: 'select', key: 'agent', label: 'Agent',
      value: filters.agent ?? null,
      options: optsWithBlank(distinct.agents, 'Wszyscy'),
      onChange: set(filters, setFilters, 'agent'),
    })
  }
  if (!omit.has('rksa') && (distinct?.rks || []).length > 0) {
    fields.push({
      type: 'select', key: 'rksa', label: 'RKSA',
      value: filters.rksa ?? null,
      options: optsWithBlank(distinct.rks, 'Wszyscy'),
      onChange: set(filters, setFilters, 'rksa'),
    })
  }
  if (!omit.has('xdm') && (distinct?.xdm || []).length > 0) {
    fields.push({
      type: 'select', key: 'xdm', label: 'XDM',
      value: filters.xdm ?? null,
      options: optsWithBlank(distinct.xdm, 'Wszyscy'),
      onChange: set(filters, setFilters, 'xdm'),
    })
  }
  return fields
}

// ─ PLK ────────────────────────────────────────────────────────
export function buildPlkFilterFields(distinct, filters, setFilters, opts = {}) {
  const omit = new Set(opts.omit || [])
  const fields = []
  if (!omit.has('pos_type') && (distinct?.pos_types || []).length > 0) {
    fields.push({
      type: 'select', key: 'pos_type', label: 'Typ salonu',
      value: filters.pos_type ?? null,
      options: optsWithBlank(distinct.pos_types),
      onChange: set(filters, setFilters, 'pos_type'),
    })
  }
  if (!omit.has('region') && (distinct?.regions || []).length > 0) {
    fields.push({
      type: 'select', key: 'region', label: 'Region',
      value: filters.region ?? null,
      options: optsWithBlank(distinct.regions),
      onChange: set(filters, setFilters, 'region'),
    })
  }
  if (!omit.has('agent') && (distinct?.agents || []).length > 0) {
    fields.push({
      type: 'select', key: 'agent', label: 'Agent',
      value: filters.agent ?? null,
      options: optsWithBlank(distinct.agents, 'Wszyscy'),
      onChange: set(filters, setFilters, 'agent'),
    })
  }
  if (!omit.has('rks') && (distinct?.rks || []).length > 0) {
    fields.push({
      type: 'select', key: 'rks', label: 'RKS',
      value: filters.rks ?? null,
      options: optsWithBlank(distinct.rks, 'Wszyscy'),
      onChange: set(filters, setFilters, 'rks'),
    })
  }
  if (!omit.has('kr') && (distinct?.kr || []).length > 0) {
    fields.push({
      type: 'select', key: 'kr', label: 'Kierownik Regionalny',
      value: filters.kr ?? null,
      options: optsWithBlank(distinct.kr, 'Wszyscy'),
      onChange: set(filters, setFilters, 'kr'),
    })
  }
  return fields
}

// ─ T-Mobile ───────────────────────────────────────────────────
export function buildTmplFilterFields(distinct, filters, setFilters, opts = {}) {
  const omit = new Set(opts.omit || [])
  const fields = []
  if (!omit.has('agent') && (distinct?.agents || []).length > 0) {
    fields.push({
      type: 'select', key: 'agent', label: 'Agent',
      value: filters.agent ?? null,
      options: optsWithBlank(distinct.agents, 'Wszyscy'),
      onChange: set(filters, setFilters, 'agent'),
    })
  }
  return fields
}
